import React, { useEffect, useState } from "react";
import { Row, Col, Empty } from "antd";
import { useTrustedNetwork } from "../../../../context/TrustedNetworkContext";
import { Loader } from "@fluentui/react-northstar";
import SettingsTrustedNetworkCard from "./SettingsTrustedNetworkCard";
import TrustNetworkConfigDialog from "../../../TrustedNetwork/components/TrustNetworkConfig/TrustNetworkConfigDialog";
import NotificationSettingsDialog from "../../../TrustedNetwork/components/Common/NotificationSettingsDialog";
import { useTranslation } from "react-i18next";
import { api } from "../../../../Assets/api/api";

const TrustedNetworkSettings = ({ user: contextUser }) => {
  const { t } = useTranslation();
  const translation = t("trustedNetwork");
  const {
    getTrustedNetworks,
    trustedNetworks,
    isLoadingTrustedNetworks,
    updateTrustedNetworkConfig,
    isUpdatingTrustedNetwork,
    user,
  } = useTrustedNetwork();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [config, setConfig] = useState({
    shareCollection: false,
    shareSpinWheel: false,
  });

  // Removed end network and info dialog state since not needed in settings

  const [isNotificationDialogOpen, setIsNotificationDialogOpen] =
    useState(false);

  const notificationOptions = [
    { label: "Share Collection", key: "shared-collection-added" },
    { label: "Share Spinwheel", key: "shared-spinWheel-added" },
  ];

  const [notificationSettings, setNotificationSettings] = useState(
    notificationOptions.reduce((acc, option) => {
      acc[`${option.key}ACV`] = false;
      acc[`${option.key}Email`] = false;
      return acc;
    }, {})
  );

  const [isNetworkSettingsSaving, setIsNetworkSettingsSaving] = useState(false);

  // Removed branches functionality - now in trusted network cards

  const isAdmin = (network) => {
    // Extract the actual slug value by removing the '?slug=' prefix
    const normalizedSlug = user.slug.replace(/^\?slug=/, "");
    // Check if the user is the creator of the network by comparing school IDs
    return network.schoolId === normalizedSlug;
  };

  // Removed info dialog handlers since not needed in settings

  useEffect(() => {
    getTrustedNetworks();
    // eslint-disable-next-line
  }, []);

  const handleOpenConfig = (network) => {
    setSelectedNetwork(network);
    // Get the current network's configuration
    const currentConfig = {
      shareCollection: network.configs[0]?.shareCollection || false,
      shareSpinWheel: network.configs[0]?.shareSpinWheel || false,
    };
    setConfig(currentConfig);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    // Prevent closing if update is in progress
    if (!isUpdatingTrustedNetwork[selectedNetwork?.id]) {
      setDialogOpen(false);
      setSelectedNetwork(null);
      setConfig({
        shareCollection: false,
        shareSpinWheel: false,
      });
    }
  };

  const [isConfigSaving, setIsConfigSaving] = useState(false);

  const handleDialogConfirm = async () => {
    if (selectedNetwork) {
      setIsConfigSaving(true);
      await updateTrustedNetworkConfig(selectedNetwork.configs[0].id, config);
      setIsConfigSaving(false);
      handleDialogClose();
    }
  };

  const handleConfigChange = (key) => {
    setConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Removed end network handlers since not needed in settings

  const handleNotificationIconClick = (network, type) => {
    fetchNotificationSettings(network, type);
    setSelectedNetwork(network);
    setIsNotificationDialogOpen(true);
  };

  // Removed branches handlers - now in trusted network cards

  const handleNotificationDialogClose = () => {
    setIsNotificationDialogOpen(false);
  };

  const fetchNotificationSettings = async (network, type) => {
    try {
      const response = await api.get(
        `/api/notification-settings/${network.id}/get${user.slug}&networkType=${type}`
      );
      const settings = response.data.settings;
      // Update state with fetched settings
      const updatedSettings = settings.reduce((acc, setting) => {
        acc[`${setting.notificationType}ACV`] = setting.inAppEnabled;
        acc[`${setting.notificationType}Email`] = setting.emailEnabled;
        return acc;
      }, {});
      setNotificationSettings(updatedSettings);
    } catch (error) {
      console.error("Error fetching notification settings:", error);
    }
  };

  const handleSaveNotificationSettings = async (network, type) => {
    setIsNetworkSettingsSaving(true);
    const updates = notificationOptions.map((option) => ({
      notificationType: option.key,
      inAppEnabled: notificationSettings[`${option.key}ACV`],
      emailEnabled: notificationSettings[`${option.key}Email`],
    }));

    try {
      await api.patch(
        `/api/notification-settings/${network.id}/update${user.slug}&networkType=${type}`,
        updates
      );
      setIsNotificationDialogOpen(false); // Close dialog after save
    } catch (error) {
      console.error("Error updating notification settings:", error);
    } finally {
      setIsNetworkSettingsSaving(false);
    }
  };

  useEffect(() => {
    if (!isNotificationDialogOpen) {
      setSelectedNetwork(null); // Reset selected network to prevent dialog from opening on refresh
    }
  }, [isNotificationDialogOpen]);

  if (isLoadingTrustedNetworks) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <Loader size="medium" />
      </div>
    );
  }

  if (!trustedNetworks || trustedNetworks.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <Empty description={translation.noNetworksFound} />
      </div>
    );
  }

  return (
    <div
      style={{ 
        height: "100vh",
        overflow: "auto",
        padding: "20px 8px 20px 8px", 
        maxWidth: "1200px", 
        margin: "0 auto",
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <h2 style={{ 
        marginBottom: "20px", 
        textAlign: "center",
        fontSize: "clamp(20px, 4vw, 24px)",
        color: "#333",
        fontWeight: "600",
        flexShrink: 0
      }}>
        Trusted Network Settings
      </h2>
      <div
        style={{
          flex: 1,
          overflow: "auto",
          paddingBottom: "20px"
        }}
      >
        <Row
          gutter={[16, 16]}
          justify="center"
          align="top"
          style={{
            width: "100%",
            margin: 0,
            display: "flex",
            flexWrap: "wrap",
            overflow: "visible",
          }}
        >
          {trustedNetworks.map((network) => (
            <Col
              key={network.id}
              xs={24}
              sm={24}
              md={12}
              lg={8}
              xl={6}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                marginBottom: "24px",
                position: "relative",
                minHeight: "auto",
                padding: "0 8px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: "350px",
                  minWidth: "260px",
                  position: "relative",
                }}
              >
                <SettingsTrustedNetworkCard
                  key={network.id}
                  schoolName={network.schoolName}
                  schoolLogo={network.schoolLogo}
                  teacherCount={network.teacherCount}
                  collectionsCount={network.collectionsCount}
                  spinWheelsCount={network.spinWheelsCount}
                  onOpenConfig={() => handleOpenConfig(network)}
                  onManageNotifications={() =>
                    handleNotificationIconClick(network, "trusted")
                  }
                />
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {selectedNetwork && (
        <TrustNetworkConfigDialog
          open={dialogOpen}
          onCancel={handleDialogClose}
          onConfirm={handleDialogConfirm}
          shareCollection={config.shareCollection}
          setShareCollection={() => handleConfigChange("shareCollection")}
          shareSpinWheel={config.shareSpinWheel}
          setShareSpinWheel={() => handleConfigChange("shareSpinWheel")}
          note={translation.updateShareNote}
          isUpdating={isConfigSaving}
        />
      )}


      <NotificationSettingsDialog
        open={isNotificationDialogOpen}
        onCancel={handleNotificationDialogClose}
        onSave={() =>
          handleSaveNotificationSettings(selectedNetwork, "trusted")
        }
        notificationSettings={notificationSettings}
        handleCheckboxChange={(key) =>
          setNotificationSettings((prev) => ({
            ...prev,
            [key]: !prev[key],
          }))
        }
        notificationOptions={notificationOptions}
        isLoading={isNetworkSettingsSaving}
        networkType={"trusted"}
      />

    </div>
  );
};

export default TrustedNetworkSettings;
